import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  createMaintenance,
  deleteMaintenance,
  getMaintenance,
  listMaintenance,
  updateMaintenance,
  type MaintenanceCreate,
  type MaintenanceListParams,
  type MaintenanceUpdate,
  type MaintenanceWindow,
} from "@/lib/api";

export const maintenanceKeys = {
  all: ["maintenance"] as const,
  list: (params?: MaintenanceListParams) =>
    [...maintenanceKeys.all, "list", params] as const,
  detail: (id: number) => [...maintenanceKeys.all, "detail", id] as const,
};

export function useMaintenance(
  params?: MaintenanceListParams,
): UseQueryResult<MaintenanceWindow[], Error> {
  return useQuery({
    queryKey: maintenanceKeys.list(params),
    queryFn: () => listMaintenance(params),
  });
}

export function useMaintenanceItem(
  id: number | null,
): UseQueryResult<MaintenanceWindow, Error> {
  return useQuery({
    queryKey: maintenanceKeys.detail(id ?? 0),
    queryFn: () => getMaintenance(id as number),
    enabled: id !== null && id !== undefined && id > 0,
  });
}

export function useCreateMaintenance(): UseMutationResult<
  MaintenanceWindow,
  Error,
  MaintenanceCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MaintenanceCreate) => createMaintenance(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.all });
    },
  });
}

export function useUpdateMaintenance(): UseMutationResult<
  MaintenanceWindow,
  Error,
  { id: number; body: MaintenanceUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateMaintenance(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.all });
    },
  });
}

export function useDeleteMaintenance(): UseMutationResult<
  void,
  Error,
  number
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteMaintenance(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: maintenanceKeys.all });
    },
  });
}
